import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// import LanguageSwitcher from '@components/common/LanguageSwitcher';
import './sidebar.scss';
import { ALL_ROUTES } from '@/Router';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';

export interface SidebarMenuItem {
  id: string;
  label: string;
  path?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  divider?: boolean;
}

export interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  menuItems?: SidebarMenuItem[];
  className?: string;
}

/**
 * Sidebar - Navigation sidebar with menu items
 *
 * Features:
 * - Slide-in/out animation
 * - Menu items with icons
 * - Navigation support
 * - Click-outside to close
 * - Keyboard support (ESC to close)
 *
 * @param isOpen - Whether sidebar is open
 * @param onClose - Callback when sidebar should close
 * @param menuItems - Array of menu items
 * @param className - Additional CSS classes
 */
export function Sidebar({
  isOpen = false,
  onClose,
  menuItems,
  className = '',
}: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Default menu items if not provided
  const defaultMenuItems: SidebarMenuItem[] = [
    {
      id: 'home',
      label: t('navigation.home'),
      path: `${ALL_ROUTES.basePath}`,
    },
    {
      id: 'percorsi',
      label: t('pages.home.menu.followPath'),
      path: `${ALL_ROUTES.percorso}`,
    },
    {
      id: 'atleti',
      label: t('pages.home.menu.chooseAthlete'),
      path: `${ALL_ROUTES.atleti}`,
    },
    {
      id: 'discipline',
      label: t('pages.home.menu.exploreByDiscipline'),
      path: `${ALL_ROUTES.discipline}`,
    },
    {
      id: 'cronologia',
      label: t('pages.home.menu.navigateInTime'),
      path: `${ALL_ROUTES.navigaNelTempo}`,
    },
    {
      id: 'divider1',
      divider: true,
      label: '',
    },
    {
      id: 'l-illustranico',
      label: t('pages.sidebar.illustrator'),
      path: `${ALL_ROUTES.illustratrice}`,
    },
    {
      id: 'la-giornalista',
      label: t('pages.sidebar.journalist'),
      path: `${ALL_ROUTES.giornalista}`,
    },
    {
      id: 'infografiche',
      label: t('pages.sidebar.infographics'),
      path: `${ALL_ROUTES.infografiche}`,
    },
    {
      id: 'tutti-gli-atleti',
      label: t('pages.sidebar.allAthletes'),
      path: `${ALL_ROUTES.tuttiGliAtleti}`,
    },
    // {
    //   id: 'quiz',
    //   label: t('pages.sidebar.quiz'),
    //   path: `${ALL_ROUTES.quiz}`,
    // },
    {
      id: 'crediti',
      label: t('pages.sidebar.credits'),
      path: `${ALL_ROUTES.crediti}`,
    },
    {
      id: 'accessibilita',
      label: t('pages.navTitles.accessibilita'),
      path: `${ALL_ROUTES.accessibilita}`,
    },
  ];

  const items = menuItems || defaultMenuItems;

  /**
   * Check if a menu item is the currently active route
   * Handles both exact matches and parent routes (e.g., /atleti/:id matches /atleti)
   */
  const isItemActive = (itemPath?: string): boolean => {
    if (!itemPath) return false;

    const currentPath = location.pathname;

    // Exact match
    if (currentPath === itemPath) return true;

    // Parent route match (e.g., /atleti is parent of /atleti/:id)
    // This is handled by checking if current path starts with the item path
    // But avoid false matches like /atleti matching /atletica
    if (itemPath !== '/' && currentPath.startsWith(itemPath + '/')) {
      return true;
    }

    // Special case for home route
    if (itemPath === '/' && currentPath === '/') {
      return true;
    }

    return false;
  };

  // Handle item click
  const handleItemClick = (item: SidebarMenuItem) => {
    if (item.divider) return;

    if (item.onClick) {
      item.onClick();
    } else if (item.path) {
      navigate(item.path);
    }

    // Close sidebar after navigation
    onClose?.();
  };

  // Close on ESC key
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent body scroll when sidebar is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="sidebar__overlay"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar ${isOpen ? 'sidebar--open' : ''} ${className}`}
        role="navigation"
        aria-label="Main navigation"
        inert={!isOpen ? true : undefined}
      >
        {/* Close button */}
        {/* <button
          className="sidebar__close"
          onClick={onClose}
          aria-label="Close menu"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button> */}

        {/* Menu items */}
        <nav className="sidebar__nav">
          {items.map((item) => {
            if (item.divider) {
              return (
                <div
                  key={item.id}
                  className="sidebar__divider"
                  aria-hidden="true"
                />
              );
            }

            return (
              <button
                key={item.id}
                className={`sidebar__item ${isItemActive(item.path) ? 'sidebar__item--active' : ''}`}
                onClick={() => handleItemClick(item)}
                aria-current={isItemActive(item.path) ? 'page' : undefined}
              >
                {item.icon && <span className="sidebar__item-icon">{item.icon}</span>}
                <span className="sidebar__item-label">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Language Switcher */}
        {/* Temporarily hidden - PM request to lock app to Italian */}
        <div className="sidebar__footer">
          <div className="sidebar__language-section">
            <LanguageSwitcher />
          </div>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
